import type {
  AdminPaginatedEnvelope,
  AfriexPromoFlags,
  AfriexPromoTags,
  AfriexRatePromo,
} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

export type TierListProps<T> = {
  /** Whatever the fetch threw. A caught value is `unknown`, not an `Error`. */
  error: unknown;
  isLoading: boolean;
  isSearch?: boolean;
  isPageReset?: boolean;
  pageInfo: {
    total: number;
    page: number;
    summary?: AdminPaginatedEnvelope<T[]>['summary'];
  };
  list: T[];
  /**
   * Async: the hook awaits the promo tier request. Declared honestly so callers
   * can sequence on it, matching `refetchCurrentPage` below.
   */
  fetchList: (params: AfriexQueryParams) => Promise<void>;
  /** Re-fetch the page the user is currently on (used after a create/update). */
  refetchCurrentPage: () => Promise<void>;
  /** 1-indexed page synced back to Pagination after a post-action re-fetch. */
  displayPage: number;
  setPage: (page: number) => void;
  filterCount: number;
  currentFilters: AfriexQueryParams;
  filtersToApply: AfriexQueryParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | Date | number | undefined,
  ) => void;
  handleApplyFilters: () => void;
  selectCurrentFilters: (
    name: string,
    value: string | Date | undefined,
  ) => void;
  handleAutoComplete: (t: string) => void;
};

export enum TierActionType {
  UPDATE_TIER = 'UPDATE_TIER',
  UPDATE_TAGS = 'UPDATE_TAGS',
  UPDATE_FLAGS = 'UPDATE_FLAGS',
  REMOVE_TAG = 'REMOVE_TAG',
}
type UpdateTierValueAction = {
  type: TierActionType.UPDATE_TIER;
  payload: Partial<AfriexRatePromo>;
};

type UpdateTierTagAction = {
  type: TierActionType.UPDATE_TAGS;
  payload: AfriexPromoTags;
};

type UpdateTierFlagAction = {
  type: TierActionType.UPDATE_FLAGS;
  payload: AfriexPromoFlags;
};

type RemoveTierTagAction = {
  type: TierActionType.REMOVE_TAG;
  payload: string;
};

export type TierAction =
  | UpdateTierValueAction
  | UpdateTierTagAction
  | UpdateTierFlagAction
  | RemoveTierTagAction;
